import React, { useState, useEffect } from 'react';
import { collection, addDoc, doc, updateDoc, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { X, Save, Wrench, Plus, Trash2, Settings, Package, RulerDimensionLine } from 'lucide-react';
import BrandBadge from './BrandBadge';
import { useActivity } from '../hooks/useActivity';

const EquipmentForm = ({ isOpen, onClose, onSuccess, editingEquipment = null }) => {
  const { currentUser, userRole } = useAuth();
  const { logEquipmentActivity } = useActivity();
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  
  // Form data state
  const [formData, setFormData] = useState({
    marcaId: '',
    tipoId: '',
    modelo: '',
    numeroParte: '',
    partesAlternativas: [''],
    especificaciones: {},
    esCustomizable: {}
  });
  
  const [errors, setErrors] = useState({});
  
  // Data collections state
  const [brands, setBrands] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [specs, setSpecs] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  // Load all required collections when form opens
  useEffect(() => {
    if (isOpen) {
      loadCollections();
    }
  }, [isOpen]);

  const loadCollections = async () => {
    try {
      setDataLoading(true);
      await Promise.all([
        fetchBrands(),
        fetchEquipmentTypes(),
        fetchSpecs()
      ]);
    } finally {
      setDataLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const q = query(collection(db, 'marcas'), orderBy('nombre'));
      const querySnapshot = await getDocs(q);
      const brandsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBrands(brandsList);
    } catch (error) {
      console.log('Marcas collection may not exist yet, starting with empty list');
      setBrands([]);
    }
  };

  const fetchEquipmentTypes = async () => {
    try {
      const q = query(collection(db, 'tipos_equipo'), orderBy('nombre'));
      const querySnapshot = await getDocs(q);
      const typesList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setEquipmentTypes(typesList);
    } catch (error) {
      console.log('Tipos_equipo collection may not exist yet, starting with empty list');
      setEquipmentTypes([]);
    }
  };

  const fetchSpecs = async () => {
    try {
      const q = query(collection(db, 'specs'), orderBy('prioridad'));
      const querySnapshot = await getDocs(q);
      const specsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSpecs(specsList);
    } catch (error) {
      console.log('Specs collection may not exist yet, starting with empty list');
      setSpecs([]);
    }
  };

  // Get available types for selected brand
  const getTypesForBrand = (brandId) => {
    if (!brandId) return [];
    
    const selectedBrand = brands.find(b => b.id === brandId);
    if (!selectedBrand) return [];
    
    // Get types that this brand can produce (using new format)
    const brandTypeIds = selectedBrand.tiposEquipoIds || selectedBrand.tiposEquipo || [];
    
    if (Array.isArray(brandTypeIds) && brandTypeIds.length > 0) {
      // Handle both new format (IDs) and old format (names) for backward compatibility
      return equipmentTypes.filter(type => {
        if (typeof brandTypeIds[0] === 'string' && brandTypeIds[0].length < 10) {
          // Old format - array of names
          return brandTypeIds.includes(type.nombre);
        } else {
          // New format - array of IDs
          return brandTypeIds.includes(type.id);
        }
      });
    }
    
    return [];
  };

  // Get specs for selected equipment type
  const getSpecsForType = (typeId) => {
    if (!typeId) return [];
    
    const selectedType = equipmentTypes.find(t => t.id === typeId);
    if (!selectedType) return [];
    
    // Get specs associated with this type (using new format)
    const typeSpecIds = selectedType.specsIds || selectedType.especificaciones || [];
    
    if (Array.isArray(typeSpecIds) && typeSpecIds.length > 0) {
      // Handle both new format (IDs) and old format (names) for backward compatibility
      if (typeof typeSpecIds[0] === 'string' && typeSpecIds[0].length > 10) {
        // New format - array of IDs
        return specs.filter(spec => typeSpecIds.includes(spec.id));
      } else {
        // Old format - array of names, convert to spec objects
        return typeSpecIds.map(specName => ({
          id: `legacy-${specName}`,
          nombre: specName,
          prioridad: 1
        }));
      }
    }
    
    return [];
  };

  // Helper functions to get names from IDs
  const getBrandNameById = (brandId) => {
    const brand = brands.find(b => b.id === brandId);
    return brand ? brand.nombre : '';
  };

  const getTypeNameById = (typeId) => {
    const type = equipmentTypes.find(t => t.id === typeId);
    return type ? type.nombre : '';
  };

  // Populate form when editing
  useEffect(() => {
    if (editingEquipment && brands.length > 0 && equipmentTypes.length > 0) {
      // Convert legacy data to new format if needed
      let marcaId = editingEquipment.marcaId || '';
      let tipoId = editingEquipment.tipoId || '';
      
      // Handle backward compatibility - convert brand name to ID
      if (!marcaId && editingEquipment.marca) {
        const brand = brands.find(b => b.nombre === editingEquipment.marca);
        marcaId = brand ? brand.id : '';
      }
      
      // Handle backward compatibility - convert type name to ID
      if (!tipoId && editingEquipment.tipo) {
        const type = equipmentTypes.find(t => t.nombre === editingEquipment.tipo);
        tipoId = type ? type.id : '';
      }
      
      setFormData({
        marcaId,
        tipoId,
        modelo: editingEquipment.modelo || '',
        numeroParte: editingEquipment.numeroParte || '',
        partesAlternativas: editingEquipment.partesAlternativas?.length > 0 ? editingEquipment.partesAlternativas : [''],
        especificaciones: editingEquipment.especificaciones || {},
        esCustomizable: editingEquipment.esCustomizable || {}
      });
      } else if (!editingEquipment) {
      // Reset form for new equipment
      setFormData({
        marcaId: '',
        tipoId: '',
        modelo: '',
        numeroParte: '',
        partesAlternativas: [''],
        especificaciones: {},
        esCustomizable: {}
      });
      }
    setErrors({});
  }, [editingEquipment, isOpen, brands, equipmentTypes]);

  // Update specs when equipment type changes
  useEffect(() => {
    if (formData.tipoId && equipmentTypes.length > 0 && specs.length > 0) {
      const availableSpecs = getSpecsForType(formData.tipoId);
      const newEspecificaciones = {};
      const newEsCustomizable = {};
      
      availableSpecs.forEach(spec => {
        const specKey = spec.nombre;
        newEspecificaciones[specKey] = formData.especificaciones[specKey] || '';
        newEsCustomizable[specKey] = formData.esCustomizable[specKey] || false;
      });
      
      setFormData(prev => ({
        ...prev,
        especificaciones: newEspecificaciones,
        esCustomizable: newEsCustomizable
      }));
    }
  }, [formData.tipoId, equipmentTypes, specs]);

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.marcaId) {
      newErrors.marcaId = 'La marca es requerida';
    }
    if (!formData.tipoId) {
      newErrors.tipoId = 'El tipo es requerido';
    }
    if (!formData.modelo.trim()) {
      newErrors.modelo = 'El modelo es requerido';
    }
    if (!formData.numeroParte.trim()) {
      newErrors.numeroParte = 'El número de parte es requerido';
    }
    
    // Validate alternate parts
    const nonEmptyAlternateParts = formData.partesAlternativas.filter(part => part.trim());
    const uniqueParts = new Set(nonEmptyAlternateParts);
    if (nonEmptyAlternateParts.length !== uniqueParts.size) {
      newErrors.partesAlternativas = 'No puede haber números de parte alternativas duplicados';
    }
    
    // Specifications are optional - no validation required
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    
    // Reset dependent fields when parent changes
    if (field === 'marcaId') {
      setFormData(prev => ({
        ...prev,
        marcaId: value,
        tipoId: '', // Reset type when brand changes
        especificaciones: {},
        esCustomizable: {}
      }));
    }
  };

  const handleSpecChange = (spec, value) => {
    setFormData(prev => ({
      ...prev,
      especificaciones: {
        ...prev.especificaciones,
        [spec]: value
      }
    }));
    
    // Clear error for this spec
    if (errors.especificaciones?.[spec]) {
      setErrors(prev => ({
        ...prev,
        especificaciones: {
          ...prev.especificaciones,
          [spec]: ''
        }
      }));
    }
  };

  const handleCustomizableToggle = (spec) => {
    setFormData(prev => ({
      ...prev,
      esCustomizable: {
        ...prev.esCustomizable,
        [spec]: !prev.esCustomizable[spec]
      }
    }));
  };

  const addAlternatePart = () => {
    setFormData(prev => ({
      ...prev,
      partesAlternativas: [...prev.partesAlternativas, '']
    }));
  };

  const removeAlternatePart = (index) => {
    if (formData.partesAlternativas.length > 1) {
      setFormData(prev => ({
        ...prev,
        partesAlternativas: prev.partesAlternativas.filter((_, i) => i !== index)
      }));
    }
  };

  const updateAlternatePart = (index, value) => {
    setFormData(prev => ({
      ...prev,
      partesAlternativas: prev.partesAlternativas.map((part, i) => 
        i === index ? value : part
      )
    }));
  };


  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    // Check permissions
    if (!userRole || !['admin', 'shopkeeper'].includes(userRole)) {
      setSubmitError(`No tienes permisos para ${editingEquipment ? 'editar' : 'crear'} equipos. Tu rol actual es: ${userRole || 'sin rol'}`);
      return;
    }

    setLoading(true);

    try {
      // Clean up data
      const cleanedData = {
        // Store only IDs - names are looked up dynamically to prevent data inconsistency
        marcaId: formData.marcaId,
        tipoId: formData.tipoId,
        modelo: formData.modelo.trim(),
        numeroParte: formData.numeroParte.trim(),
        partesAlternativas: formData.partesAlternativas
          .filter(part => part.trim())
          .map(part => part.trim()),
        especificaciones: Object.fromEntries(
          Object.entries(formData.especificaciones)
            .filter(([_, value]) => value?.toString().trim())
            .map(([key, value]) => [key, value.toString().trim()])
        ),
        esCustomizable: formData.esCustomizable,
        updatedAt: new Date()
      };

      const isEditing = !!editingEquipment;
      let savedEquipmentId;
      
      if (editingEquipment) {
        // Update existing equipment
        await updateDoc(doc(db, 'equipment_catalog', editingEquipment.id), cleanedData);
        savedEquipmentId = editingEquipment.id;
      } else {
        // Create new equipment
        cleanedData.isArchived = false;
        cleanedData.createdAt = new Date();
        const docRef = await addDoc(collection(db, 'equipment_catalog'), cleanedData);
        savedEquipmentId = docRef.id;
      }
      
      // Invalidate equipment cache to ensure fresh data everywhere
      try {
        const { invalidateCache } = await import('../lib/cacheService');
        invalidateCache('equipment');
      } catch (error) {
        console.error('Error invalidating equipment cache:', error);
      }
      
      // Log activity
      try {
        const equipmentModel = cleanedData.modelo || 'Modelo sin especificar';
        const brandName = getBrandNameById(cleanedData.marcaId) || 'Sin marca';
        const typeName = getTypeNameById(cleanedData.tipoId) || 'Sin tipo';
        
        await logEquipmentActivity(
          isEditing ? 'updated' : 'created',
          equipmentModel,
          '',
          {
            equipmentId: savedEquipmentId,
            marca: brandName,
            tipo: typeName,
            numeroParte: cleanedData.numeroParte
          }
        );
      } catch (error) {
        console.error('Error logging equipment activity:', error);
        // Don't fail equipment creation if activity logging fails
      }
      
      // Get the equipment with populated brand and type names for the callback
      if (onSuccess) {
        try {
          // Create equipment object with populated names
          const brandName = getBrandNameById(cleanedData.marcaId);
          const typeName = getTypeNameById(cleanedData.tipoId);
          
          const equipmentWithNames = {
            id: savedEquipmentId,
            ...cleanedData,
            marca: brandName,
            tipo: typeName
          };
          
          onSuccess(equipmentWithNames);
        } catch (error) {
          console.error('Error preparing equipment data for callback:', error);
          onSuccess();
        }
      }
      
      // Reset form and close after callback
      resetForm();
      onClose();
    } catch (error) {
      console.error('Error saving equipment:', error);
      setSubmitError('Error al guardar el equipo. Por favor intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      marcaId: '',
      tipoId: '',
      modelo: '',
      numeroParte: '',
      partesAlternativas: [''],
      especificaciones: {},
      esCustomizable: {}
    });
    setErrors({});
    setSubmitError('');
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  if (!isOpen) return null;

  const renderFormContent = () => {
    if (dataLoading) {
      return (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          Cargando datos...
        </div>
      );
    }

    const availableTypes = getTypesForBrand(formData.marcaId);
    const availableSpecs = getSpecsForType(formData.tipoId);

    return (
      <div className="space-y-8">
        {/* Brand and Type Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Brand Selection */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
              <Package className="h-5 w-5 text-purple-600 dark:text-purple-400 mr-2" />
              Marca del Equipo
            </h3>
            {brands.length === 0 ? (
              <div className="text-center py-6 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600 rounded-lg">
                <Package className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">No hay marcas disponibles</p>
                <p className="text-xs">Primero debe crear marcas en la gestión de marcas.</p>
              </div>
            ) : (
              <div>
                <select
                  id="marcaId"
                  value={formData.marcaId}
                  onChange={(e) => handleInputChange('marcaId', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                    errors.marcaId ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  disabled={loading}
                >
                  <option value="">Seleccione una marca...</option>
                  {brands.map(brand => (
                    <option key={brand.id} value={brand.id}>{brand.nombre}</option>
                  ))}
                </select>
                {errors.marcaId && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.marcaId}</p>
                )}
                
                {/* Selected Brand Preview */}
                {formData.marcaId && (
                  <div className="mt-2 flex items-center space-x-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Vista previa:</span>
                    <BrandBadge 
                      brand={brands.find(b => b.id === formData.marcaId)} 
                      size="sm" 
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Type Selection */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
              <Settings className="h-5 w-5 text-purple-600 dark:text-purple-400 mr-2" />
              Tipo de Equipo
            </h3>
            {!formData.marcaId ? (
              <div className="text-center py-6 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600 rounded-lg">
                <Settings className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                <p className="text-xs">Seleccione una marca primero</p>
              </div>
            ) : availableTypes.length === 0 ? (
              <div className="text-center py-6 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600 rounded-lg">
                <Settings className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">No hay tipos disponibles</p>
                <p className="text-xs">La marca no tiene tipos asociados.</p>
              </div>
            ) : (
              <div>
                <select
                  id="tipoId"
                  value={formData.tipoId}
                  onChange={(e) => handleInputChange('tipoId', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                    errors.tipoId ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  disabled={loading}
                >
                  <option value="">Seleccione un tipo...</option>
                  {availableTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.nombre}</option>
                  ))}
                </select>
                {errors.tipoId && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.tipoId}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Model and Part Numbers */}
        <div className="space-y-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
            <Wrench className="h-5 w-5 text-purple-600 dark:text-purple-400 mr-2" />
            Modelo y Números de Parte
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="modelo" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Modelo *
              </label>
              <input
                type="text"
                id="modelo"
                value={formData.modelo}
                onChange={(e) => handleInputChange('modelo', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                  errors.modelo ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="Ej: SQ3-55"
                disabled={loading}
              />
              {errors.modelo && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.modelo}</p>
              )}
            </div>

            <div>
              <label htmlFor="numeroParte" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Número de Parte Principal *
              </label>
              <input
                type="text"
                id="numeroParte"
                value={formData.numeroParte}
                onChange={(e) => handleInputChange('numeroParte', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                  errors.numeroParte ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="Ej: 96160120"
                disabled={loading}
              />
              {errors.numeroParte && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.numeroParte}</p>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Números de Parte Alternativos
              </label>
              <button
                type="button"
                onClick={addAlternatePart}
                className="inline-flex items-center space-x-1 px-3 py-1 bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 rounded-md hover:bg-purple-200 dark:hover:bg-purple-900/30 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 text-sm"
                disabled={loading}
              >
                <Plus className="h-4 w-4" />
                <span>Agregar</span>
              </button>
            </div>
            
            {errors.partesAlternativas && (
              <div className="mb-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-3 py-2 rounded-md text-sm">
                {errors.partesAlternativas}
              </div>
            )}
            
            <div className="space-y-2">
              {formData.partesAlternativas.map((part, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={part}
                    onChange={(e) => updateAlternatePart(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder={`Número de parte alternativo ${index + 1}`}
                    disabled={loading}
                  />
                  {formData.partesAlternativas.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeAlternatePart(index)}
                      className="p-2 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                      title="Eliminar"
                      disabled={loading}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Specifications */}
        <div className="space-y-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
            <RulerDimensionLine className="h-5 w-5 text-purple-600 dark:text-purple-400 mr-2" />
            Especificaciones Técnicas
          </h3>
          
          {!formData.tipoId ? (
            <div className="text-center py-6 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600 rounded-lg">
              <RulerDimensionLine className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-xs">Seleccione una marca y tipo de equipo primero</p>
            </div>
          ) : availableSpecs.length === 0 ? (
            <div className="text-center py-6 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600 rounded-lg">
              <RulerDimensionLine className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">No hay especificaciones definidas</p>
              <p className="text-xs">El tipo de equipo no tiene especificaciones asociadas.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4">
                <p className="text-sm text-blue-800 dark:text-blue-400">
                  <strong>Especificaciones para:</strong> {getBrandNameById(formData.marcaId)} - {getTypeNameById(formData.tipoId)}
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                  Las especificaciones son opcionales. Puede dejarlas vacías y completarlas después.
                </p>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {availableSpecs.map(spec => (
                  <div key={spec.id || spec.nombre} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {spec.nombre}
                      </label>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Customizable</span>
                        <input
                          type="checkbox"
                          checked={formData.esCustomizable[spec.nombre] || false}
                          onChange={() => handleCustomizableToggle(spec.nombre)}
                          className="h-4 w-4 text-purple-600 dark:text-purple-400 focus:ring-purple-500 dark:focus:ring-purple-400 border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600"
                          disabled={loading}
                        />
                      </div>
                    </div>
                    
                    {spec.descripcion && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{spec.descripcion}</p>
                    )}
                    
                    <input
                      type="text"
                      value={formData.especificaciones[spec.nombre] || ''}
                      onChange={(e) => handleSpecChange(spec.nombre, e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 bg-white dark:bg-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                        errors.especificaciones?.[spec.nombre] ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-500'
                      }`}
                      placeholder={formData.esCustomizable[spec.nombre] ? 'Valor por defecto (opcional)' : 'Dejar vacío o ingresar valor (opcional)'}
                      disabled={loading}
                    />
                    
                    {errors.especificaciones?.[spec.nombre] && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.especificaciones[spec.nombre]}</p>
                    )}
                    
                    {formData.esCustomizable[spec.nombre] && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Se puede personalizar por orden de trabajo
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Wrench className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingEquipment ? 'Editar Equipo' : 'Nuevo Equipo'}
              </h2>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          
        </div>

        <div className="p-6">
          {submitError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md mb-6">
              {submitError}
            </div>
          )}
          {renderFormContent()}
        </div>

        <div className="p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 disabled:opacity-50"
            >
              Cancelar
            </button>
            
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 dark:bg-purple-700 text-white rounded-md hover:bg-purple-700 dark:hover:bg-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              <span>{loading ? 'Guardando...' : (editingEquipment ? 'Actualizar Equipo' : 'Guardar Equipo')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquipmentForm;